import { Box, Paper, PaperProps, styled, keyframes } from "@mui/material";
import { forwardRef, useCallback } from "react";
import { ListChildComponentProps } from "react-window";
import { BoxWithX } from "src/components/FlightStatus/BoxWithX";
import { NoCardFound } from "src/components/FlightStatus/cards/NoCardFound";
import chroma from "chroma-js";

export interface IFlightStatusRowRendererProps extends ListChildComponentProps {
  data: {
    rows: any[];
    columnDefs: any;
    componentMap: { [key: string]: React.ComponentType<any> };
    useUTC: "local" | "UTC";
    flightFilter: any[];
    setFlightFilter: React.Dispatch<React.SetStateAction<any[]>>;
    filteredFlightIds: string[];
    sizeMap: React.MutableRefObject<{ [key: number]: number }>;
    setRowSize: (index: number, size: number) => void;
  };
}

const glow = keyframes`
    0% {box-shadow: 0 0 0px rgba(255, 0, 0, 0.5), 0 0 0px rgba(255, 0, 0, 0.4), 0 0 0px rgba(255, 0, 0, 0.3);
    100% {box-shadow: 0 0 3px rgba(255, 0, 0, 1), 0 0 3px rgba(255, 0, 0, 0.8), 0 0 3px rgba(255, 0, 0, 0.6);}
    `;

const FlightStatusRow = styled(
  forwardRef<HTMLDivElement, PaperProps & { style?: React.CSSProperties; data?: any; index?: number }>((props, ref) => (
    <Paper {...props} ref={ref} />
  ))
)(({ theme }) => ({
  padding: "4px",
  boxSizing: "border-box",
  border: `2px solid ${theme.palette.background.paper}`,
  backgroundColor: theme.palette.background.paper,
  borderLeft: `16px solid ${theme.palette.info.dark}`,
  borderLeftRadius: 8,
  display: "flex",
  "&:hover": {
    backgroundColor: chroma(theme.palette.background.paper).brighten(0.2).css(),
  },
  "&.errorState": {
    border: `1px solid ${theme.palette.error.dark}`,
    borderLeft: "16px solid red",
    backgroundColor: chroma(theme.palette.error.dark).darken(3).css(),
    animation: `${glow} 2s infinite alternate`,
  },
}));

export const FlightStatusRowRenderer: React.FC<IFlightStatusRowRendererProps> = ({ index, style, data }) => {
  const {
    rows,
    columnDefs,
    componentMap,
    useUTC,
    flightFilter,
    setFlightFilter,
    filteredFlightIds,
    sizeMap,
    setRowSize,
  } = data;
  const row = rows[index];
  const rowRef = useCallback(
    (node: any) => {
      if (node) {
        const height = node.getBoundingClientRect().height;
        setTimeout(() => {
          if (height !== sizeMap.current[index]) {
            setRowSize(index, height);
          }
        }, 0);
      }
    },
    [index]
  );
  if (!row) return null;

  if ((filteredFlightIds.length > 0 && filteredFlightIds.includes(row.flightId)) || filteredFlightIds.length === 0) {
    return (
      <FlightStatusRow
        ref={rowRef}
        style={style}
        className={row.hasOwnProperty("errorState") && Object.keys(row.errorState).length > 0 ? "errorState" : ""}
      >
        <Box sx={{ height: "100%", display: "flex", alignItems: "stretch" }}>
          {Object.keys(row).map((componentName: string, componentKey: number) => {
            if (componentName !== "flightId" && componentName !== "errorState") {
              const Component = componentMap[componentName];
              if (Component) {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "stretch",
                      "&>div": {
                        paddingLeft: "10px",
                        paddingRight: "10px",
                        boxSizing: "border-box",
                        borderRight: "1px solid #111",
                      },
                    }}
                    key={componentKey}
                  >
                    <Box sx={{ width: columnDefs[componentName].width ?? "200px", flexGrow: 1 }}>
                      {row[componentName] ? (
                        //@ts-ignore
                        <Component
                          data={row[componentName]}
                          flightId={row.flightId}
                          useUTC={useUTC}
                          flightFilter={flightFilter}
                          setFlightFilter={setFlightFilter}
                        />
                      ) : (
                        <>
                          <BoxWithX />
                        </>
                      )}
                    </Box>
                  </Box>
                );
              } else {
                return <NoCardFound componentName={componentName} key={componentKey} />;
              }
            } else {
              return null;
            }
          })}
        </Box>
      </FlightStatusRow>
    );
  } else {
    return null;
  }
};
